import { c as _c } from "react/compiler-runtime";
import { Tooltip, ActionIcon, Alert, Overlay } from '@mantine/core';
import { RichText, EditorCmd } from 'components/form/RichText';
import { usePortal } from 'components/portal/portal';
import { SAvatar } from 'components/portal/SAvatar';
import { MdCheckCircle, MdOutlineClear, MdSend, MdError } from 'react-icons/md';
import { sortBy } from 'remeda';
import { colors } from 'components/theme/theme';
import { StyleProps } from 'components';
import { CommentStatusIcon, StatusButton, statusIconProps, StatusType } from './CommentStatus';
import { KbdComboTip, KbdCombo, useKbd } from 'components/Keyboard';
import { CommentCfg, zCommentCfg } from './commentTypes';
import { UseUIItem } from 'components/hooks/useUIState';
import { twMerge } from 'tailwind-merge';
import { useMutation } from '@tanstack/react-query';
const shortcuts = {
  post: {
    key: 'Enter',
    mods: ['Meta']
  }
} satisfies Record<string, KbdCombo>;
export type ComposeProps = {
  stored?: CommentCfg;
  readonly: boolean;
  showAvatar?: boolean;
  hideBorder?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  showStatus?: boolean;
  ui: UseUIItem<typeof zCommentCfg>;
  onPost: (comment: CommentCfg) => Promise<void>;
  onBlur?: (props: {
    isDirty: boolean;
  }) => void;
  onCancel?: () => void;
  debugName?: string;
} & StyleProps;
export const Compose = t0 => {
  const $ = _c(59);
  const {
    stored,
    onPost,
    onBlur,
    onCancel,
    placeholder: t1,
    readonly,
    showAvatar,
    hideBorder,
    autoFocus,
    showStatus,
    ui,
    className,
    debugName
  } = t0;
  const placeholder = t1 === undefined ? "Post a new comment" : t1;
  const {
    values,
    setValues,
    del: clear,
    isDirtyVsInitial: isDirty
  } = ui;
  const {
    profile
  } = usePortal();
  const {
    kbdMatch
  } = useKbd();
  let t2;
  if ($[0] !== clear || $[1] !== stored) {
    t2 = () => {
      if (!stored) {
        clear();
      }
    };
    $[0] = clear;
    $[1] = stored;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onPost || $[4] !== t2) {
    t3 = {
      mutationFn: onPost,
      onSuccess: t2
    };
    $[3] = onPost;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const postMutation = useMutation(t3);
  let t4;
  if ($[6] !== postMutation || $[7] !== values) {
    t4 = () => {
      if (!values) {
        return;
      }
      postMutation.mutate(values);
    };
    $[6] = postMutation;
    $[7] = values;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const _onPost = t4;
  if (!values) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <></>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  const enabled = !readonly && isDirty && values.text != "";
  const editorReadonly = readonly && !isDirty;
  const t5 = `flex w-full gap-2 ${className} relative`;
  let t6;
  if ($[10] !== profile || $[11] !== showAvatar) {
    t6 = showAvatar && <SAvatar size="sm" profile={profile} className="mt-[0.3em]" />;
    $[10] = profile;
    $[11] = showAvatar;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = hideBorder && "border-y-0";
  let t8;
  if ($[13] !== t7) {
    t8 = twMerge("grow-1", t7);
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== setValues) {
    t9 = md => {
      setValues({
        text: md
      });
    };
    $[15] = setValues;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== _onPost || $[18] !== enabled || $[19] !== kbdMatch) {
    t10 = (_, e) => {
      if (kbdMatch(e, shortcuts.post) && enabled) {
        _onPost();
      }
    };
    $[17] = _onPost;
    $[18] = enabled;
    $[19] = kbdMatch;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isDirty || $[22] !== onBlur) {
    t11 = () => {
      onBlur?.({
        isDirty
      });
    };
    $[21] = isDirty;
    $[22] = onBlur;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== _onPost || $[25] !== clear || $[26] !== enabled || $[27] !== isDirty || $[28] !== onCancel || $[29] !== postMutation.isPending || $[30] !== readonly || $[31] !== setValues || $[32] !== showStatus || $[33] !== values.resolved || $[34] !== values.status) {
    t12 = __0 => !readonly && <><div className="flex items-center justify-end"><Tooltip label={<KbdComboTip title="Cancel" combo={{
          key: "Escape"
        }} />} withinPortal={true}><ActionIcon style={{
            visibility: isDirty ? "visible" : "hidden"
          }} onClick={() => {
            clear();
            onCancel?.();
          }}><MdOutlineClear size={20} style={{
              color: isDirty ? colors.primary[5] : colors.gray[3]
            }} /></ActionIcon></Tooltip>{values.status?.map(s => <CommentStatusIcon key={s} {...statusIconProps(s as StatusType)} resolved={values.resolved} />)}{values.resolved && <CommentStatusIcon Icon={MdCheckCircle} label="Resolved" color={colors.green[5]} resolved={values.resolved} />}{showStatus && isDirty ? <StatusButton values={values.status ?? []} setStatus={status => setValues({
          status: sortBy(status, _temp) as StatusType[]
        })} /> : null}<Tooltip label={<KbdComboTip title="Post" combo={shortcuts.post} />} withinPortal={true}><div><ActionIcon loading={postMutation.isPending} disabled={!enabled} onClick={() => {
              _onPost();
            }}><MdSend size={20} style={{
                color: enabled ? colors.primary[5] : colors.gray[3]
              }} /></ActionIcon></div></Tooltip></div></>;
    $[24] = _onPost;
    $[25] = clear;
    $[26] = enabled;
    $[27] = isDirty;
    $[28] = onCancel;
    $[29] = postMutation.isPending;
    $[30] = readonly;
    $[31] = setValues;
    $[32] = showStatus;
    $[33] = values.resolved;
    $[34] = values.status;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== autoFocus || $[37] !== debugName || $[38] !== editorReadonly || $[39] !== placeholder || $[40] !== t10 || $[41] !== t11 || $[42] !== t12 || $[43] !== t8 || $[44] !== t9 || $[45] !== values.text) {
    t13 = <RichText className={t8} value={values.text} onChange={t9} onKeyDown={t10} onBlur={t11} autoFocus={autoFocus} placeholder={placeholder} readonly={editorReadonly} syncValue={true} debugName={debugName}>{t12}</RichText>;
    $[36] = autoFocus;
    $[37] = debugName;
    $[38] = editorReadonly;
    $[39] = placeholder;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t8;
    $[44] = t9;
    $[45] = values.text;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== postMutation.isPending) {
    t14 = postMutation.isPending && <Overlay color="var(--mantine-color-body)" backgroundOpacity={0.35} />;
    $[47] = postMutation.isPending;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t13 || $[50] !== t14 || $[51] !== t5 || $[52] !== t6) {
    t15 = <div className={t5}>{t6}{t13}{t14}</div>;
    $[49] = t13;
    $[50] = t14;
    $[51] = t5;
    $[52] = t6;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== postMutation) {
    t16 = postMutation.isError && <Alert title="Error posting comment" color="red" className="mb-2" onClose={() => postMutation.reset()}>{postMutation.error?.message || "An error occurred while posting your comment"}</Alert>;
    $[54] = postMutation;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== t15 || $[57] !== t16) {
    t17 = <>{t15}{t16}</>;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  return t17;
};
function _temp(s_0) {
  return s_0;
}